import api from './api';

/*
 * r16p (2026-10-24), DESIGN D:/NecroWork/dm-chats-evidence/DESIGN.md 6.6: the V3 chat routes. Session + CSRF only (lib/api.js); the
 * server scopes every answer to the logged-in account, admins included (O11), and refuses Basic on these paths. Nothing here filters
 * for access.
 *
 *   GET    /uiapi/chats/info                      {enabled, platforms, ai:{enabled, configured, usedToday, limit, processorNotice},
 *                                                  retentionDays, maxReplyLength, newlines, cadenceNote}
 *   GET    /uiapi/chats/summary                   {unreadThreads, needsReply, slotsWithUnread, queuedReplies, heldReplies,
 *                                                  unconfirmedReplies, failedReplies, asOf}
 *   GET    /uiapi/chats/threads?slot=&filter=&cursor=&limit=   {threads:[...], nextCursor, slot?}
 *   GET    /uiapi/chats/threads/{id}?before=      {thread, messages[], replies[], olderCursor, lastSeq, slot?}
 *   POST   /uiapi/chats/threads/{id}/read         moves the dashboard's own read marker (not Instagram's)
 *   PUT    /uiapi/chats/threads/{id}              {neverOpen}
 *   DELETE /uiapi/chats/threads/{id}              hard delete; 409 while a reply is claimed or sending
 *   POST   /uiapi/chats/threads/{id}/replies      {text, clientRequestId, basisSeq, suggestionId?, sendIfNewInbound?, allowRepeat?}
 *   POST   /uiapi/chats/replies/{id}/release      HELD -> QUEUED ("Send anyway" / "Confirm")
 *   DELETE /uiapi/chats/replies/{id}              CANCELLED, or 409 reply_already_claimed
 *   POST   /uiapi/chats/replies/{id}/requeue      a NEW reply from an unconfirmed or failed one (the worker looks for the text first)
 *   POST   /uiapi/chats/threads/{id}/suggest      {jobId}
 *   GET    /uiapi/chats/suggestions/{jobId}       {state, suggestions:[{text}], ai:{usedToday, limit}}
 *   GET    /uiapi/chats/settings                  r16pm: {retentionDays, retentionChoices, aiDailyCap, dailySendCap, hourlySendCap,
 *                                                  repliesDisabled, editable}
 *   PUT    /uiapi/chats/settings                  r16pm: {retentionDays} (7 / 30 / 90; 422 retention_invalid) -> the same view
 *   DELETE /uiapi/chats/slots/{slot}/data         r16pm: erase a slot's chat data -> {erased, rows, settingsReset, readsAgainIf771Ticked}
 *   GET    /uiapi/api-keys, POST /uiapi/api-keys, DELETE /uiapi/api-keys/{id}
 *   POST   /uiapi/api-access/lock
 *
 * Ids in paths are the server's opaque public ids (conv_..., rpl_...), never a handle (design 7.1: no handle in a URL). The create
 * answer of a key is NOT cached anywhere: callers use a plain useMutation and keep the key in React state only (design 7.4).
 */
const enc = encodeURIComponent;
// the READS answer a 401 to the page ("no inbox for this login") instead of lib/api.js's global /login redirect (review: the summary
// is polled on every page; a login type with no chat access must not bounce the dashboard). Writes keep the redirect.
const QUIET = { necroNoAuthRedirect: true };

export const chatsApi = {
  info: () => api.get('/chats/info', QUIET),
  summary: () => api.get('/chats/summary', QUIET),
  threads: ({ slot, filter, cursor, limit = 50 } = {}) => {
    const params = { limit };
    if (slot) params.slot = slot;
    if (filter) params.filter = filter;
    if (cursor) params.cursor = cursor;
    return api.get('/chats/threads', { ...QUIET, params });
  },
  thread: (id, before) => api.get(`/chats/threads/${enc(id)}`, before ? { ...QUIET, params: { before } } : QUIET),
  // r16q7: Live mode. Every answer: {enabled, session: {id, slot, state, threadId, reason, note, idleMinutes, idleLeftSec}|null}
  liveGet: (slot) => api.get('/chats/live', { ...QUIET, params: { slot } }),
  liveStart: (slot, threadId) => api.post('/chats/live', threadId ? { slot, threadId } : { slot }, QUIET),
  liveFocus: (id, threadId) => api.put(`/chats/live/${enc(id)}`, { threadId }, QUIET),
  liveActivity: (id) => api.post(`/chats/live/${enc(id)}/activity`, null, QUIET),
  liveEnd: (id) => api.delete(`/chats/live/${enc(id)}`, QUIET),
  liveInput: (id, input) => api.post(`/chats/live/${enc(id)}/input`, input, QUIET),
  // r16q5: up to 50 chat pictures in one answer: {pics: {id: base64 JPEG}}
  pics: (ids) => api.get('/chats/pics', { ...QUIET, params: { ids: ids.join(',') } }),
  markRead: (id, seq) => api.post(`/chats/threads/${enc(id)}/read`, seq === undefined || seq === null ? {} : { seq }),
  setNeverOpen: (id, neverOpen) => api.put(`/chats/threads/${enc(id)}`, { neverOpen: !!neverOpen }),
  deleteThread: (id) => api.delete(`/chats/threads/${enc(id)}`),
  queueReply: (id, body) => api.post(`/chats/threads/${enc(id)}/replies`, body),
  releaseReply: (id) => api.post(`/chats/replies/${enc(id)}/release`, {}),
  cancelReply: (id) => api.delete(`/chats/replies/${enc(id)}`),
  requeueReply: (id) => api.post(`/chats/replies/${enc(id)}/requeue`, {}),
  suggest: (id) => api.post(`/chats/threads/${enc(id)}/suggest`, {}),
  suggestion: (jobId) => api.get(`/chats/suggestions/${enc(jobId)}`),
  // r16pm (design 6.6 / 6.10 / 8.7): the account's retention and the slot-data erase (pages/ChatDataPage.js). The GET is a read
  // (no /login bounce, like the other reads); the PUT and the DELETE are writes and keep the redirect.
  settings: () => api.get('/chats/settings', QUIET),
  saveSettings: (retentionDays) => api.put('/chats/settings', { retentionDays }),
  eraseSlotData: (slot) => api.delete(`/chats/slots/${enc(slot)}/data`),
};

export const apiKeysApi = {
  list: () => api.get('/api-keys'),
  create: (body) => api.post('/api-keys', body),
  revoke: (id) => api.delete(`/api-keys/${enc(id)}`),
  lockAccess: () => api.post('/api-access/lock', {}),
};
